import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { createJob, listJobs, type JobInput } from '../api/endpoints.js';
import { SyncStatusPill } from '../components/SyncStatusPill.js';
import { errorMessage } from '../api/client.js';
import { fmtDate } from '../lib/format.js';

const EMPTY_FORM: JobInput = {
  title: '',
  descriptionHtml: '',
  country: 'US',
  cityRegionPostal: '',
  contactEmail: '',
  contactPhone: '',
};

export function JobsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const jobsQuery = useQuery({ queryKey: ['jobs'], queryFn: listJobs });

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<JobInput>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (input: JobInput) => createJob(input),
    onSuccess: (job) => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      navigate(`/jobs/${job.id}`);
    },
    onError: (err) => setFormError(errorMessage(err)),
  });

  function set<K extends keyof JobInput>(key: K, value: JobInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    createMutation.mutate({
      ...form,
      contactPhone: form.contactPhone || null,
    });
  }

  const jobs = jobsQuery.data ?? [];

  return (
    <div className="page">
      <header className="page__header">
        <h1>Jobs</h1>
        <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
          New job
        </button>
      </header>

      {jobsQuery.isLoading && <div className="spinner" aria-label="Loading jobs" />}
      {jobsQuery.isError && <div className="alert alert--error">Failed to load jobs.</div>}

      {!jobsQuery.isLoading && jobs.length === 0 && (
        <div className="empty-state">
          <h2>No jobs yet</h2>
          <p>Create your first SDR posting to start collecting applications.</p>
        </div>
      )}

      {jobs.length > 0 && (
        <div className="card">
          <table className="table table--hover">
            <thead>
              <tr>
                <th>Title</th>
                <th>Location</th>
                <th>Status</th>
                <th>Indeed</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id}>
                  <td>
                    <Link to={`/jobs/${job.id}`}>{job.title}</Link>
                  </td>
                  <td>
                    {job.cityRegionPostal}, {job.country}
                  </td>
                  <td>
                    <span className={`pill job-${job.status}`}>{job.status}</span>
                  </td>
                  <td>
                    <SyncStatusPill status={job.indeedSyncStatus} />
                  </td>
                  <td>{fmtDate(job.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label="Create job"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Create job</h2>
            {formError && <div className="alert alert--error">{formError}</div>}
            <form onSubmit={onSubmit} className="form-grid">
              <label className="field field--full">
                <span className="field__label">Title</span>
                <input
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  required
                  placeholder="Sales Development Representative"
                />
              </label>
              <label className="field">
                <span className="field__label">City / region / postal</span>
                <input
                  value={form.cityRegionPostal}
                  onChange={(e) => set('cityRegionPostal', e.target.value)}
                  required
                  placeholder="Austin, TX 78701"
                />
              </label>
              <label className="field">
                <span className="field__label">Country</span>
                <input
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                  required
                  maxLength={2}
                  placeholder="US"
                />
              </label>
              <label className="field">
                <span className="field__label">Contact email</span>
                <input
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => set('contactEmail', e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field__label">Contact phone (optional)</span>
                <input
                  value={form.contactPhone ?? ''}
                  onChange={(e) => set('contactPhone', e.target.value)}
                />
              </label>
              <label className="field field--full">
                <span className="field__label">Description (HTML)</span>
                <textarea
                  value={form.descriptionHtml}
                  onChange={(e) => set('descriptionHtml', e.target.value)}
                  rows={8}
                  required
                  placeholder="<p>We're hiring SDRs…</p>"
                />
              </label>
              <div className="modal__actions">
                <button type="button" className="btn btn--ghost" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating…' : 'Create job'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
